import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { CheckCircle, XCircle, Loader2, ArrowRight } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';

const VerifyEmailPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage('Token verifikasi tidak ditemukan.');
      return;
    }

    verifyEmail();
  }, [token]);

  const verifyEmail = async () => {
    try {
      const response = await axios.post(`${API}/auth/verify-email?token=${token}`);
      
      if (response.data.success) {
        setStatus('success');
        setMessage(response.data.message);
        setEmail(response.data.email);
        toast.success('Email berhasil diverifikasi!');
      }
    } catch (error) {
      setStatus('error');
      setMessage(error.response?.data?.detail || 'Verifikasi gagal. Token mungkin sudah kadaluarsa.');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#0A0A0A] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white dark:bg-zinc-900 rounded-xl shadow-xl p-8 text-center">
          
          {/* Verifying State */}
          {status === 'verifying' && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 bg-slate-100 dark:bg-zinc-800 rounded-full flex items-center justify-center">
                <Loader2 className="w-10 h-10 text-indigo-600 animate-spin dark:text-indigo-400" />
              </div>
              <h1 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-zinc-50 mb-2" style={{ fontFamily: 'Manrope' }}>
                Memverifikasi...
              </h1>
              <p className="text-slate-600 dark:text-zinc-400">
                Mohon tunggu sebentar
              </p>
            </>
          )}

          {/* Success State */}
          {status === 'success' && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 bg-indigo-100 dark:bg-indigo-950/40 rounded-full flex items-center justify-center">
                <CheckCircle className="w-10 h-10 text-indigo-600 dark:text-indigo-400" />
              </div>
              <h1 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-zinc-50 mb-2" style={{ fontFamily: 'Manrope' }}>
                Email Terverifikasi!
              </h1>
              <p className="text-slate-600 dark:text-zinc-400 mb-6">
                {message}
              </p>
              
              {email && (
                <div className="bg-slate-100 dark:bg-zinc-800 rounded-lg px-4 py-3 mb-6 overflow-hidden">
                  <p className="text-sm text-slate-500 dark:text-zinc-400">Email:</p>
                  <p className="font-medium text-slate-900 dark:text-zinc-50 break-all text-sm">{email}</p>
                </div>
              )}

              <Button
                onClick={() => navigate('/login')}
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white"
                data-testid="go-to-login-btn"
              >
                Lanjut ke Login
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </>
          )}

          {/* Error State */}
          {status === 'error' && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                <XCircle className="w-10 h-10 text-red-600 dark:text-red-400" />
              </div>
              <h1 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-zinc-50 mb-2" style={{ fontFamily: 'Manrope' }}>
                Verifikasi Gagal
              </h1>
              <p className="text-slate-600 dark:text-zinc-400 mb-6">
                {message}
              </p>

              <div className="space-y-3">
                <Button
                  onClick={() => navigate('/register')}
                  variant="outline"
                  className="w-full"
                >
                  Daftar Ulang
                </Button>
                <Link 
                  to="/login" 
                  className="block text-sm text-indigo-600 hover:underline dark:text-indigo-400"
                >
                  Sudah punya akun? Login
                </Link>
              </div>
            </>
          )}
        </div>

        {/* Logo */}
        <div className="text-center mt-8">
          <Link to="/" className="inline-flex items-center gap-2">
            <img src="/images/logo.png" alt="Nerava" className="w-8 h-8" />
            <span className="text-lg font-bold text-slate-900 dark:text-zinc-50" style={{ fontFamily: 'Manrope' }}>
              Nerava
            </span>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmailPage;
